import { JSX, Suspense, useMemo, useState } from "react";

import { Box, Grid, Paper, Theme, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSummarizedHistoryChartQuery } from "src/api/locationChartQueries.api";
import CardHeader from "src/components/CardHeader/CardHeader";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import { ComponentParams } from "src/components/Dashboard/interfaces/DashboardComponentParams";
import DownloadCSV from "src/components/DownloadCSV/DownloadCSV";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import MetricHeader from "src/components/MetricHeader/MetricHeader";
import MetricLeftBlock from "src/components/MetricHeader/MetricLeftBlock";
import TableView from "src/components/TableView/TableView";
import ComponentId from "src/constants/componentId.enum";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { mapRegionFootfall } from "src/lib/chart.helper";
import { getDateTime, getIntervalFromSelectedTab, getMeasuresData, getToday } from "src/lib/date.helper";
import {
  filterByLocationTypeAndSortMeasuresSummarizedDto,
  formatNum,
  getCurrentTotalCount,
  getDifference,
  getPastTotalCount,
} from "src/lib/number.helpers";
import useIntervalTabsStore from "src/store/intervalTabs.store";
import { type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";

import FootfallViewSwitch from "./FootfallViewSwitch";
import FootfallViewType from "./footfallVileType.enum";
import CenterMap from "./Map/CenterMap";

const getStyles = (theme: Theme) => ({
  fullWidth: { xs: 12, md: 12 },
  tableGrid: (selectedViewType: FootfallViewType) =>
    selectedViewType === FootfallViewType.Map ? { height: 580, width: "100%" } : { height: "100%" },
  alignEnd: { flexGrow: "grow", display: "flex", justifyContent: "end", alignItems: "end" },
  currentValue: { fontSize: theme.fontSizes.display },
  pastValue: { fontSize: theme.fontSizes.display, fontWeight: theme.typography.fontWeightRegular },
  pastValueText: { fontSize: theme.fontSizes.md, fontWeight: theme.typography.fontWeightLight },
});

const rows = [10];

function Footfall({ selectedLocation, displayedType, componentId }: ComponentParams): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { componentStates, setView } = useIntervalTabsStore();

  const stateForPassedId = componentStates[componentId] ?? {
    selectedTab: IntervalTab.Day,
    selectedDate: undefined,
  };
  const stateForCurrentComponent = componentStates[ComponentId.Footfall] ?? { view: FootfallViewType.Map };

  const componentState = {
    ...stateForPassedId,
    view: stateForCurrentComponent.view,
  };

  const selectedDate = componentState.selectedDate ? dayjs(componentState.selectedDate) : null;

  const [selectedViewType, setSelectedViewType] = useState<FootfallViewType>(Number(componentState.view));

  const { data: measuresDataToday } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Day,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataWeek } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Week,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataMonth } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.Month,
    selectedDate,
    MeasureType.Count
  );
  const { data: measuresDataYear } = useSummarizedHistoryChartQuery(
    [selectedLocation.Id],
    IntervalType.IsoYear,
    selectedDate,
    MeasureType.Count
  );

  const measuresData = useMemo(
    () => getMeasuresData(componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear),
    [componentState.selectedTab, measuresDataToday, measuresDataWeek, measuresDataMonth, measuresDataYear]
  ) as MeasuresSummarizedDto;

  const filteredMeasuresData = useMemo(
    () => filterByLocationTypeAndSortMeasuresSummarizedDto(measuresData, displayedType),
    [displayedType, measuresData]
  );
  const diff = useMemo(() => getDifference(filteredMeasuresData), [filteredMeasuresData]);
  const totalCurrentValue = useMemo(
    () => (filteredMeasuresData?.data ? getCurrentTotalCount(filteredMeasuresData.data) : 0),
    [filteredMeasuresData]
  );
  const totalPastValue = useMemo(
    () => (filteredMeasuresData?.data ? getPastTotalCount(filteredMeasuresData.data) : 0),
    [filteredMeasuresData]
  );

  const intervalType = getIntervalFromSelectedTab(componentState.selectedTab);

  const dataRows = useMemo(
    () => (filteredMeasuresData ? mapRegionFootfall(filteredMeasuresData, intervalType) : []),
    [filteredMeasuresData, intervalType]
  );

  return (
    <Paper sx={sharedStyles(theme).cardContainer}>
      <CardHeader header={t(`footfall`)} dateTime={getDateTime(filteredMeasuresData, componentState.selectedTab, selectedDate ?? getToday(), t)} />
      <MetricHeader
        leftContent={
          <MetricLeftBlock
            variant='footfall'
            totalCurrentValue={formatNum(totalCurrentValue)}
            totalPastValue={formatNum(totalPastValue)}
            diff={diff}
            pastLabel={t("previous year")}
          />
        }
        rightContent={
          <>
            <FootfallViewSwitch
              componentId={ComponentId.Footfall}
              selectedViewType={selectedViewType}
              setSelectedViewType={setSelectedViewType}
              setView={setView}
            />
            <Box display='flex' alignItems='center' ml={1}>
              <DownloadCSV componentId={ComponentId.Footfall} locationName={selectedLocation.LocalName} rows={dataRows} />
            </Box>
          </>
        }
      />

      <Grid container spacing={2}>
        <Grid size={getStyles(theme).fullWidth} sx={getStyles(theme).tableGrid(selectedViewType)}>
          <Suspense fallback={<ChartSkeleton />}>
            {!filteredMeasuresData && <ChartSkeleton />}
            {filteredMeasuresData && selectedViewType === FootfallViewType.List && (
              <TableView measuresData={filteredMeasuresData} rows={rows} showActions displayedType={displayedType} />
            )}
            {filteredMeasuresData && selectedViewType === FootfallViewType.Map && (
              <CenterMap measuresData={filteredMeasuresData} selectedTab={componentState.selectedTab} displayedType={displayedType} />
            )}
          </Suspense>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default Footfall;
